import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Facebook, MessageCircle, Copy, Newspaper, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function ProBlogShare() {
  const [ctx, setCtx] = useState(null);
  const [source, setSource] = useState("mine");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

  useEffect(() => {
    api.get("/fb-posts/context").then((r) => setCtx(r.data.entity || { kind: "pro" })).catch(() => setCtx({ kind: "pro" }));
  }, []);

  const ownKind = ctx && (ctx.kind === "club" || ctx.kind === "association");
  const isMember = ctx && ctx.kind === "member";
  const mine = ownKind && source === "mine";

  useEffect(() => {
    if (!ctx) return;
    setLoading(true);
    const url = mine
      ? (ctx.kind === "club" ? "/clubs/me/news" : "/associations/me/news")
      : "/blog?limit=60";
    api.get(url)
      .then((r) => setItems(r.data?.items || r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [ctx, source]); // eslint-disable-line

  // URL affichée (page app) et URL de partage (page OG soignée) distinctes.
  const viewUrl = (p) => mine
    ? `${ORIGIN}/${ctx.kind === "club" ? "clubs" : "associations"}/${ctx.slug}/actualites/${p.slug}`
    : `${ORIGIN}/blog/${p.slug}`;
  const shareUrl = (p) => mine
    ? `${BACKEND}/api/entity-news-vitrine/${ctx.kind === "club" ? "clubs" : "associations"}/${ctx.slug}/${p.slug}`
    : `${BACKEND}/api/blog-vitrine/${p.slug}`;
  const count = (p, channel) => { if (!mine) api.post(`/blog/${p.slug}/share`, { channel }).catch(() => {}); };
  const fb = (p) => { count(p, "facebook"); window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl(p))}`, "_blank", "noopener,noreferrer"); };
  const wa = (p) => { count(p, "whatsapp"); window.open(`https://wa.me/?text=${encodeURIComponent(`${p.title}\n${shareUrl(p)}`)}`, "_blank", "noopener,noreferrer"); };
  const copy = async (p) => { count(p, "copy"); try { await navigator.clipboard.writeText(shareUrl(p)); toast.success("Lien copié !"); } catch { toast.info(shareUrl(p)); } };

  const tabCls = (active) => `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold ${active ? "bg-[#E05638] text-white" : "text-slate-600 hover:bg-slate-100"}`;

  return (
    <div data-testid="pro-blog-share">
      <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2">
        <Newspaper className="text-[#E05638]" size={24} /> Partager un article de blog
      </h1>
      <p className="text-slate-500 text-sm mt-1 max-w-2xl">
        {isMember
          ? "Relayez les articles du blog local dans vos groupes Facebook, sur WhatsApp ou par lien. L'aperçu affiche l'image et le titre."
          : "Relayez vos actualités dans vos groupes Facebook, sur WhatsApp ou par lien. L'aperçu affiche l'image et le titre."}
      </p>

      {ownKind && (
        <div className="mt-3 inline-flex rounded-xl bg-white border border-slate-200 p-1 gap-1" data-testid="pro-blog-source-toggle">
          <button onClick={() => setSource("mine")} className={tabCls(source === "mine")} data-testid="pro-blog-source-mine"><Newspaper size={15} /> Mes actualités</button>
          <button onClick={() => setSource("platform")} className={tabCls(source === "platform")} data-testid="pro-blog-source-platform">Blog PROXIreduc</button>
        </div>
      )}

      {loading ? <div className="mt-6 text-slate-500 text-sm">Chargement…</div> :
       items.length === 0 ? <div className="mt-6 text-slate-500 text-sm">{mine ? "Vous n'avez pas encore d'actualité — publiez-en dans « Mes actualités »." : "Aucun article disponible pour le moment."}</div> :
       <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
         {items.map((p) => (
           <div key={p.id || p.slug} className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col" data-testid={`pro-share-article-${p.slug}`}>
             <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
               {p.cover_image ? <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" /> :
                 <div className="w-full h-full grid place-items-center text-slate-300"><Newspaper size={32} /></div>}
             </div>
             <div className="p-4 flex-1 flex flex-col">
               {(p.category || p.city) && <div className="text-[11px] font-bold uppercase tracking-widest text-rose-700 truncate">{[p.category, p.city].filter(Boolean).join(" · ")}</div>}
               <h3 className="mt-1 font-heading font-bold text-slate-900 line-clamp-2">{p.title}</h3>
               {p.excerpt && <p className="mt-1 text-xs text-slate-500 line-clamp-2">{p.excerpt}</p>}
               <a href={viewUrl(p)} target="_blank" rel="noreferrer" className="mt-2 text-xs text-blue-700 font-semibold inline-flex items-center gap-1 hover:underline" data-testid={`pro-share-article-open-${p.slug}`}>
                 Voir en ligne <ExternalLink size={11} />
               </a>
               <div className="mt-auto pt-3 border-t border-slate-100 flex gap-2">
                 <Button size="sm" onClick={() => fb(p)} className="bg-[#1877F2] hover:bg-[#1465d8] text-white flex-1" data-testid={`pro-share-article-fb-${p.slug}`}>
                   <Facebook size={14} className="mr-1" /> Facebook
                 </Button>
                 <Button size="sm" variant="outline" onClick={() => wa(p)} className="text-green-700 border-green-300" data-testid={`pro-share-article-wa-${p.slug}`}><MessageCircle size={14} /></Button>
                 <Button size="sm" variant="outline" onClick={() => copy(p)} data-testid={`pro-share-article-copy-${p.slug}`}><Copy size={14} /></Button>
               </div>
             </div>
           </div>
         ))}
       </div>}
    </div>
  );
}
